import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import liffService from 'src/services/liff'
import { APP_LINE_OA_ID } from 'src/constants/env-constant'
import { PATH as PATH_MY_PAGE } from 'src/pages/myPage'
import { LINE_STATUS, LineStatus } from 'src/constants/user'

import {
  CloseOutlined,
  UserAddOutlined,
  FileTextOutlined,
  PhoneOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { Typography, Button, Skeleton } from 'antd'
import { linkLineByLiff } from 'src/useCase/line'
import appActions from 'src/store/ui/app'
import { useAppDispatch, useAppSelector } from 'src/store'
import { useNavigate } from 'react-router-dom'
import iconEmblem from 'src/assets/images/landing/icon-emblem.svg'
import { SOCKET_EVENT } from 'src/containers/Chat/constants'
import { createEchoConnection } from 'src/utils/echoService'
import uiActions from 'src/store/ui/notification'
import { getInfo } from 'src/useCase/user'

const { Title, Text } = Typography

const LineLinkContainer: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [lineStatus, setLineStatus] = useState<LineStatus | null>(null)
  const [isWaitingForFollow, setIsWaitingForFollow] = useState(false)
  const user = useAppSelector((state) => state.ui.app.authInfo)
  const followCheckInterval = useRef<ReturnType<typeof setInterval> | null>(
    null,
  )
  const followTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const followUrl = `https://line.me/R/ti/p/@${APP_LINE_OA_ID}`

  const handleOauthCancel = () => {
    const params = new URLSearchParams(window.location.search)
    const oauthError = params.get('error')

    if (oauthError === 'ACCESS_DENIED') {
      sessionStorage.removeItem('line_link_screen_visited')
      window.history.replaceState({}, document.title, window.location.pathname)
      navigate(PATH_MY_PAGE.CONTACT_METHOD, { replace: true })
      return true
    }

    return false
  }

  const initLiffFlow = async () => {
    try {
      await liffService.init()

      if (!liffService.isLogin) {
        handleLiffLogin()
        return
      }

      await handleLineLink()
    } catch {
      return false
    }
  }

  const handleLiffLogin = () => {
    const visited =
      sessionStorage.getItem('line_link_screen_visited') === 'true'

    if (visited) {
      sessionStorage.removeItem('line_link_screen_visited')
      return navigate(PATH_MY_PAGE.CONTACT_METHOD)
    }

    sessionStorage.setItem('line_link_screen_visited', 'true')
    liffService.login()
  }

  const handleLineLink = async () => {
    try {
      const res = await dispatch(linkLineByLiff()).unwrap()

      if (res.line_status === LINE_STATUS.LINKED_AND_FOLLOWED) {
        return navigate(PATH_MY_PAGE.CONTACT_METHOD)
      }

      if (res.line_status === LINE_STATUS.LINKED_NOT_FOLLOWED) {
        setLineStatus(LINE_STATUS.LINKED_NOT_FOLLOWED)
      }
    } catch (error: any) {
      handleLineLinkError(error)
    }
  }

  const handleLineLinkError = (error: any) => {
    if (
      error?.code === 'errors.invalid_authorization_line_code' ||
      error?.code === 'errors.duplicate_line_id'
    ) {
      if (error?.code === 'errors.invalid_authorization_line_code') {
        liffService.logout()
      }

      return navigate(PATH_MY_PAGE.CONTACT_METHOD)
    }

    if (error?.line_status === LINE_STATUS.LINKED_NOT_FOLLOWED) {
      setLineStatus(LINE_STATUS.LINKED_NOT_FOLLOWED)
    }
  }

  useEffect(() => {
    if (handleOauthCancel()) return
    initLiffFlow()
  }, [])

  const handleAddFriend = () => {
    setIsWaitingForFollow(true)
    window.open(followUrl, '_blank')

    followCheckInterval.current = setInterval(async () => {
      try {
        const resUserInfo = await dispatch(getInfo({})).unwrap()
        const currentLineStatus = resUserInfo?.line_status

        if (currentLineStatus === LINE_STATUS.LINKED_AND_FOLLOWED) {
          stopFollowChecking()

          dispatch(
            uiActions.onNotification({
              type: 'success',
              message: 'LINE連携が完了しました。',
            }),
          )

          return navigate(PATH_MY_PAGE.CONTACT_METHOD)
        }
      } catch {
        return false
      }
    }, 10000)

    followTimeout.current = setTimeout(() => {
      stopFollowChecking()

      dispatch(
        uiActions.onNotification({
          type: 'warning',
          message:
            'LINEの友だち追加が確認できませんでした。QRコードをスキャンして再度お試しください。',
        }),
      )

      setIsWaitingForFollow(false)
    }, 120000)
  }

  const handleGetFriendInfo = async () => {
    try {
      const res = await dispatch(linkLineByLiff()).unwrap()

      if (res.line_status === LINE_STATUS.LINKED_AND_FOLLOWED) {
        return navigate(PATH_MY_PAGE.CONTACT_METHOD)
      }
    } catch (error) {
      return false
    }
  }

  const stopFollowChecking = () => {
    if (followCheckInterval.current) {
      clearInterval(followCheckInterval.current)
      followCheckInterval.current = null
    }

    if (followTimeout.current) {
      clearTimeout(followTimeout.current)
      followTimeout.current = null
    }
  }

  useEffect(() => {
    if (!user?.id) return

    const echo = createEchoConnection()
    const channelName = `private-user.${user.id}`

    echo.channel(channelName).listen(SOCKET_EVENT.LINE_FOLLOWED, ({ data }) => {
      if (data?.status === LINE_STATUS.LINKED_AND_FOLLOWED) {
        stopFollowChecking()

        setIsWaitingForFollow(false)

        dispatch(appActions.patchAuthInfo({ line_status: data.status }))

        dispatch(
          uiActions.onNotification({
            type: 'success',
            message: 'LINE連携が完了しました。',
          }),
        )
        return navigate(PATH_MY_PAGE.CONTACT_METHOD)
      }
    })

    return () => {
      echo.leaveChannel(channelName)
      stopFollowChecking()
    }
  }, [user?.id, navigate, dispatch])

  if (!lineStatus) return <Container />

  const handleClose = () => {
    if (isWaitingForFollow) {
      stopFollowChecking()
      setIsWaitingForFollow(false)
      return
    }

    return navigate(PATH_MY_PAGE.CONTACT_METHOD)
  }

  return (
    <Container>
      <Card>
        <Header>
          <CloseOutlined onClick={handleClose} />
        </Header>

        {isWaitingForFollow ? (
          <>
            <Profile style={{ padding: '0 20px' }}>
              <Skeleton.Avatar active size={80} shape="circle" />
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginTop: 20,
                }}
              >
                <Skeleton.Button
                  active
                  style={{ width: '40%', height: 24, marginBottom: 12 }}
                />
                <Skeleton.Button
                  active
                  style={{ width: '30%', height: 16, marginBottom: 12 }}
                />
                <Skeleton.Button active style={{ width: '60%', height: 16 }} />
              </div>
            </Profile>

            <ActionGrid>
              <ActionItem>
                <Skeleton.Avatar
                  active
                  size={24}
                  shape="circle"
                  style={{ marginBottom: 6 }}
                />
                <Skeleton.Button active style={{ width: '40%', height: 16 }} />
              </ActionItem>
              <ActionItem>
                <Skeleton.Avatar
                  active
                  size={24}
                  shape="circle"
                  style={{ marginBottom: 6 }}
                />
                <Skeleton.Button active style={{ width: '40%', height: 16 }} />
              </ActionItem>
            </ActionGrid>

            <div style={{ padding: '20px' }}>
              <Skeleton.Button
                active
                style={{ width: '30%', height: 20, marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <Skeleton.Avatar active size={18} shape="circle" />
                <Skeleton.Button active style={{ width: '70%', height: 18 }} />
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <Skeleton.Avatar active size={18} shape="circle" />
                <Skeleton.Button active style={{ width: '40%', height: 18 }} />
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <Skeleton.Avatar active size={18} shape="circle" />
                <Skeleton.Button active style={{ width: '80%', height: 18 }} />
              </div>
            </div>

            <Footer>
              <Skeleton.Button
                active
                block
                style={{ height: 48, borderRadius: 8 }}
              />
            </Footer>
          </>
        ) : (
          <>
            <Profile>
              <Avatar src={iconEmblem} />

              <Title level={4} style={{ marginTop: 10 }}>
                平川市
              </Title>

              <Text type="secondary">平川市 公式</Text>

              <Desc>平川市の公式アカウントです。</Desc>
            </Profile>

            <ActionGrid>
              <ActionItem active onClick={handleAddFriend}>
                <UserAddOutlined />
                <Text>追加</Text>
              </ActionItem>

              <ActionItem>
                <FileTextOutlined />
                <Text>投稿</Text>
              </ActionItem>
            </ActionGrid>

            <InfoSection>
              <Title level={5}>基本情報</Title>

              <InfoItem>
                <InfoCircleOutlined />
                <Text>平川市の公式アカウントです。</Text>
              </InfoItem>

              <InfoItem>
                <PhoneOutlined />
                <Text>0172-44-1111</Text>
              </InfoItem>

              <InfoItem>
                <GlobalOutlined />
                <Text>http://www.city.hirakawa.lg.jp</Text>
              </InfoItem>
            </InfoSection>

            <Footer>
              <Button
                type="primary"
                size="large"
                block
                onClick={handleGetFriendInfo}
                style={{
                  background: '#06C755',
                  border: 'none',
                  height: 48,
                  borderRadius: 8,
                }}
              >
                友だち限定の情報をゲット
              </Button>
            </Footer>
          </>
        )}
      </Card>
    </Container>
  )
}

export default LineLinkContainer

const Container = styled.div`
  display: flex;
  justify-content: center;
  background: #f6f6f6;
  min-height: 100vh;
  align-items: center;
`

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: white;
`

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px;
  font-size: 18px;
`

const Profile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const Avatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
`

const Desc = styled.div`
  margin-top: 6px;
  font-size: 14px;
  color: #666;
`

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-top: 20px;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
`

const ActionItem = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  cursor: pointer;

  border-right: 1px solid #eee;
  border-bottom: ${(props) => (props.active ? '3px solid #06C755' : 'none')};

  .anticon {
    font-size: 22px;
    margin-bottom: 6px;
  }
`

const InfoSection = styled.div`
  padding: 20px;
`

const InfoItem = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;

  .anticon {
    font-size: 18px;
    color: #666;
  }
`

const Footer = styled.div`
  padding: 20px;
`
